import React, { useCallback, useState } from 'react';
import { bool, node, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  CustomExtendedDataField,
  Icons,
  NamedLink,
} from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';
import TalentFormCard from './TalentFormCard';
import { LocationSectionFields, TalentSignupStep, UserType } from '../../../util/enums';
import UserTypeSpecificFields from './UserTypeSpecificFields';
import { insertCustomFields } from '../../../util/editListingHelpers';
import { divisionSchoolOptions } from '../../../config/configExtras';
import FanProfileFormFields from './FanProfileFormFields';
import DefaultUserInfoFields from '../../EditListingPage/EditListingWizard/EditListingDetailsPanel/DefaultUserInfoFields';
import RenderLocationSections from '../../EditListingPage/EditListingWizard/EditListingDetailsPanel/RenderLocationSections';
import RenderCustomLinks from './RenderCustomLinks';
import SchoolInputFormFields from './SchoolInputFormFields';
import AgencyFormInputFields from './AgencyFormInputFields';
import BrandFormFields from './BrandFormFields';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

// Helper function to get schools based on selected division and conference
export const getSchools = (division, conference) => {
  return divisionSchoolOptions.filter(
    school => school.division === division && school.conference === conference
  );
};
const MAX_BIO_LENGTH = 350;

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    keepDirtyOnReinitialize
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        preselectedUserType,
        userTypes,
        userFields,
        values,
        form,
        config,
        history,
        viewport,
        form: {
          change,
          batch,
          mutators: { push, remove },
        },
      } = formRenderProps;
      const { userType, description: initialDescription = '' } = values || {};

      // Local state to manage the bio
      const [bio, setBio] = useState(initialDescription || '');

      // Handle changes to the bio text
      const handleBioChange = useCallback(
        e => {
          let newValue = e.target.value;

          // If user exceeds 350 chars, truncate to 350
          if (newValue.length > MAX_BIO_LENGTH) {
            newValue = newValue.slice(0, MAX_BIO_LENGTH);
          }

          setBio(newValue);
          form.change('description', newValue); // Sync with form state
        },
        [form]
      );

      // Calculate remaining characters (will not go below 0)
      const remainingChars = Math.max(0, MAX_BIO_LENGTH - bio?.length);
      const { FINAL, INITIAL } = TalentSignupStep;
      const [currentStep, setCurrentStep] = useState(INITIAL);
      const { AGENCY, BRAND, FAN, SCHOOL, TALENT } = UserType;
      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequirementMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequirements',
        defaultMessage:
          'Password must be 8-30 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.',
      });

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);
      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const updatedUserFieldProps = insertCustomFields(userFieldProps)?.filter(
        e => !['pub_state', 'pub_job_title'].includes(e.key)
      );

      const locationSectionsFieldProps = updatedUserFieldProps.filter(f =>
        LocationSectionFields.includes(f.key)
      );

      const formComponents = {
        [AGENCY]: AgencyFormInputFields,
        [BRAND]: BrandFormFields,
      };

      const SelectedFormFields = formComponents[userType];

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              {![TALENT, SCHOOL, AGENCY, BRAND].includes(userType) && (
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <FieldTextInput
                    className={css.formFld}
                    type="email"
                    id={formId ? `${formId}.email` : 'email'}
                    name="email"
                    autoComplete="email"
                    label={intl.formatMessage({
                      id: 'SignupForm.emailLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.emailPlaceholder',
                    })}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                </div>
              )}
              {![TALENT, SCHOOL, AGENCY, BRAND].includes(userType) && (
                <div className={css.formRow}>
                  <FieldTextInput
                    className={css.formFld}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="given-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.firstNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.firstNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.firstNameRequired',
                      })
                    )}
                  />
                  <FieldTextInput
                    className={css.formFld}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.lastNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.lastNameRequired',
                      })
                    )}
                  />
                </div>
              )}
              {userType === UserType.TALENT && (
                <>
                  <DefaultUserInfoFields
                    intl={intl}
                    userType={userType}
                    userTypeConfig={userTypeConfig}
                    currentStep={currentStep}
                    formId={formId}
                  />
                </>
              )}
              {SelectedFormFields && (
                <SelectedFormFields
                  emailFormatValid={emailValid}
                  emailRequired={emailRequired}
                  intl={intl}
                  formId={formId}
                  classes={classes}
                  handleBioChange={handleBioChange}
                  remainingChars={remainingChars}
                  bio={bio}
                />
              )}

              {![TALENT, SCHOOL, AGENCY, BRAND].includes(userType) && (
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <UserFieldPhoneNumber
                    formName="SignupForm"
                    className={css.formFld}
                    rootClassName={css.formFld}
                    userTypeConfig={userTypeConfig}
                    intl={intl}
                  />
                </div>
              )}
              {currentStep === FINAL && userType === UserType.TALENT ? (
                <RenderLocationSections
                  locationSectionsFieldProps={locationSectionsFieldProps}
                  currentStep={currentStep}
                  intl={intl}
                  formId={formId}
                  values={values}
                  userType={userType}
                  FINAL={FINAL}
                  form={form}
                />
              ) : null}
              {currentStep === FINAL ? (
                <h1 className={css.affiliationInfo}>
                  {' '}
                  {intl.formatMessage({
                    id: 'EditListingDetailsForm.affiliationInfo',
                  })}
                </h1>
              ) : null}

              {showCustomUserFields && userType !== FAN ? (
                <div className={css.customFields}>
                  {updatedUserFieldProps.map(fieldProps => (
                    <CustomExtendedDataField
                      {...fieldProps}
                      formId={formId}
                      form={form}
                      values={values}
                      currentStep={currentStep}
                      viewport={viewport}
                    />
                  ))}
                </div>
              ) : null}
              {userType === SCHOOL && (
                <SchoolInputFormFields
                  emailFormatValid={emailValid}
                  emailRequired={emailRequired}
                  intl={intl}
                  formId={formId}
                  classes={classes}
                  handleBioChange={handleBioChange}
                  remainingChars={remainingChars}
                  bio={bio}
                />
              )}

              {currentStep === FINAL && (
                <>
                  <FieldTextInput
                    className={css.customField}
                    type="textarea"
                    name="description"
                    id="description"
                    label={intl.formatMessage({
                      id: 'SignupForm.descriptionLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.descriptionPlaceholder',
                    })}
                    value={bio}
                    onChange={handleBioChange}
                  />
                  <div
                    className={classNames(css.characterCount, {
                      [css.warning]: remainingChars < 10 && remainingChars > 0,
                      [css.limitReached]: remainingChars === 0,
                    })}
                  >
                    {remainingChars}{' '}
                    {intl.formatMessage({
                      id: 'SignupForm.descriptionCharactersLeft',
                    })}
                  </div>
                </>
              )}
              {currentStep === FINAL && userType === TALENT && (
                <RenderCustomLinks viewport={viewport} />
              )}

              {![BRAND, AGENCY, SCHOOL, FAN].includes(userType) &&
                (userType !== TALENT || currentStep === FINAL) && (
                  <TalentFormCard
                    form={form}
                    values={values}
                    push={push}
                    remove={remove}
                    config={config}
                    viewport={viewport}
                    isSignupPage={true}
                  />
                )}

              {[BRAND, AGENCY, SCHOOL].includes(userType) && (
                <UserTypeSpecificFields
                  userType={userType}
                  form={form}
                  values={values}
                  intl={intl}
                  config={config}
                />
              )}
              <UserFieldDisplayName
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
              {userType === UserType.FAN && (
                <FanProfileFormFields
                  intl={intl}
                  formId={formId}
                  values={values}
                  form={form}
                  handleBioChange={handleBioChange}
                  remainingChars={remainingChars}
                  bio={bio}
                />
              )}
              {(userType !== TALENT || currentStep === FINAL) && (
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <FieldTextInput
                    className={css.formFld}
                    type="password"
                    id={formId ? `${formId}.password` : 'password'}
                    name="password"
                    autoComplete="new-password"
                    label={intl.formatMessage({
                      id: 'SignupForm.passwordLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.passwordPlaceholder',
                    })}
                    validate={validators.passwordValidators(intl)}
                  />
                  <ul className={css.passwordRequirements}>{passwordRequirementMessage}</ul>
                </div>
              )}
            </div>
          ) : null}

          {userType ? (
            <>
              <div className={css.bottomWrapper}>
                {(userType !== TALENT || currentStep === FINAL) && termsAndConditions}
                <div className={css.formBtns}>
                  <div
                    className={css.goBack}
                    onClick={() =>
                      currentStep === FINAL ? setCurrentStep(INITIAL) : history.goBack()
                    }
                  >
                    <Icons name="back" />
                    <FormattedMessage id="AuthenticationPage.goBack" />
                  </div>
                  <PrimaryButton
                    className={css.submitBtn}
                    onClick={() =>
                      userType !== TALENT || currentStep === INITIAL
                        ? setCurrentStep(FINAL)
                        : handleSubmit(values)
                    }
                    type={userType === TALENT ? 'button' : 'submit'}
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                  >
                    <FormattedMessage
                      id={
                        userType === TALENT && currentStep === INITIAL
                          ? 'SignupForm.next'
                          : 'SignupForm.signUp'
                      }
                    />
                  </PrimaryButton>
                </div>
              </div>
              <div className={css.loginText}>
                <FormattedMessage id="SignupForm.alreadyHaveAccount" />{' '}
                <NamedLink name="LoginPage">
                  <FormattedMessage id="SignupForm.loginLink" />
                </NamedLink>
              </div>
            </>
          ) : null}
        </Form>
      );
    }}
  />
);

SignupFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  inProgress: false,
  preselectedUserType: null,
};

SignupFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  inProgress: bool,
  termsAndConditions: node.isRequired,
  preselectedUserType: string,
  userTypes: propTypes.userTypes.isRequired,
  userFields: propTypes.listingFields.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
